import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  BaseAgency,
  Entitlement,
  ENTITLEMENTS,
  INDIVIDUAL_SMALLGROUP_REGION_ENUM,
  LINE_OF_BUSINESS_ENUM,
  LOGIN_INFO_REDUCER_NAME,
  SENIOR_REGION_ENUM,
  UserInfoStateStructure,
  USER_ROLE
} from '@ptl/account-management';
import { Subscription } from 'rxjs';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { environment } from 'src/environments/environment';
import { NpnAndFFMChangeModalComponent } from './npn-and-ffmchange-modal/npn-and-ffmchange-modal.component';

interface CertificationInterface {
  name: string;
  startDate: string;
  endDate: string;
}

interface EntitlementsInfoFormControls {
  lineOfBusiness: AbstractControl;
  region: AbstractControl;
  agency: AbstractControl;
}

interface EntitlementsInfoFormValuesAsJsonInterface {
  lineOfBusiness: LINE_OF_BUSINESS_ENUM;
  region: string;
  agency: BaseAgency;
}
@Component({
    selector: 'app-agent-information',
    templateUrl: './agent-information.component.html',
    styleUrls: ['./agent-information.component.scss'],
    standalone: false
})
export class AgentInformationComponent implements OnInit, OnDestroy {

  @Input() content: { [contentKey: string]: string | boolean } = {
    myProfileTitle: undefined,
    myProfileContent: undefined,
    myProfileInfoTitle: undefined,
    myProfileIsEditAvailable: undefined,
    showFirstName: undefined,
    showLastName: undefined,
    showAddress: undefined,
    showZipCounty: undefined,
    showDob: undefined,
    showGender: undefined,
    showEmail: undefined,
    showPhoneNumber: undefined,
    showPassword: undefined,
    sectionTitle: undefined
  };

  /**
   * Need the following:
   *
   */

  user: UserInfoStateStructure;
  validCertifications: CertificationInterface[];
  activeEntitlements: Entitlement[];

  isMinnesotaRegionSelected: boolean;

  contentStorageKey = 'myProfile-agentInformation';
  selectedAgencySubscription: Subscription;

  entitlementsInfoForm: UntypedFormGroup;
  availableRegions: (SENIOR_REGION_ENUM | INDIVIDUAL_SMALLGROUP_REGION_ENUM)[];
  availableAgencies: BaseAgency[];
  availableLinesOfBusiness: LINE_OF_BUSINESS_ENUM[];

  lineOfBusinessControlChangesSubscription: Subscription;
  regionControlChangesSubscription: Subscription;
  entitlementsInfoFormChangesSubscription: Subscription;
  userType: string;
  npn: string;
  ffmId: string;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private fb: UntypedFormBuilder,
    private matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.isMinnesotaRegionSelected = environment.initData.brand.ASPID === 'MN';

    const userData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(
      LOGIN_INFO_REDUCER_NAME
    );

    this.user = userData;
    this.npn = userData.userInfo.npnNo;
    this.ffmId = userData.userInfo.ffmId;

    this.availableLinesOfBusiness = this.getAvailableLinesOfBusiness();
    this.validCertifications = this.getValidCertifications();

    this.createForm();
    this.setupFormSubscribers();
  }

  ngOnDestroy() {
    if (this.lineOfBusinessControlChangesSubscription) {
      this.lineOfBusinessControlChangesSubscription.unsubscribe();
    }
    if (this.regionControlChangesSubscription) {
      this.regionControlChangesSubscription.unsubscribe();
    }
    if (this.entitlementsInfoFormChangesSubscription) {
      this.entitlementsInfoFormChangesSubscription.unsubscribe();
    }
  }
  editFFMInfo(npn: string, ffmId: string) {

    this.matDialog.open(NpnAndFFMChangeModalComponent, {
      maxWidth: '650px',
      maxHeight: '100vh',
      data: {
        npn, ffmId
      }
    });
  }
  createForm() {
    this.entitlementsInfoForm = this.fb.group({
      lineOfBusiness: this.fb.control(''),
      region: this.fb.control(''),
      agency: this.fb.control('')
    });
    this.formControls.region.disable({ emitEvent: false });
    this.formControls.agency.disable({ emitEvent: false });
  }

  setupFormSubscribers() {
    /** Line of Business control changed */
    this.lineOfBusinessControlChangesSubscription = this.formControls.lineOfBusiness.valueChanges.subscribe(
      v => {
        console.log('expect first');
        this.formControls.region.setValue('', { emitEvent: false });
        this.formControls.agency.setValue('', { emitEvent: false });
      }
    );

    /** Region control changed */
    this.regionControlChangesSubscription = this.formControls.region.valueChanges.subscribe(
      v => {
        this.formControls.agency.setValue('', { emitEvent: false });
      }
    );
    /** Global FormGroup changed */
    this.entitlementsInfoFormChangesSubscription = this.entitlementsInfoForm.valueChanges.subscribe(
      (formValue: EntitlementsInfoFormValuesAsJsonInterface) => {
        console.log('expect second');
        if (formValue.agency) {
          this.setupUserTypeForDisplay();
          const agencyWithEntitlements = this.getSelectedAgencyWithEntitlements()
          this.activeEntitlements = this.getActiveEntitlements(agencyWithEntitlements);
        } else {
          this.activeEntitlements = [];
        }

        let lobFilteredAgencies: BaseAgency[] = [];

        if (formValue.lineOfBusiness) {
          lobFilteredAgencies = (() => {
            switch (formValue.lineOfBusiness) {
              case LINE_OF_BUSINESS_ENUM.INDIVIDUAL:
                if (environment.initData.brand.ASPID !== "HM") {
                  return this.user.userInfo.agencies;
                } else {
                  return this.user.userInfo.broker360ACAAgencies;
                }
              case LINE_OF_BUSINESS_ENUM.SENIOR:
                return this.user.userInfo.broker360Agencies;
              case LINE_OF_BUSINESS_ENUM.SMALL_GROUP:
                return this.user.userInfo.agencies;
            }
          })();

          this.availableRegions = (() => {
            /** return unique regions */
            return (lobFilteredAgencies as BaseAgency[])
              .map(a => a.region)
              .filter((item, index, array) => item && array.indexOf(item) === index);
          })();

          if (this.availableRegions.length > 0) {
            this.formControls.region.enable({emitEvent: false});
            this.formControls.agency.disable({emitEvent: false});
          }
        }

        if (formValue.region && lobFilteredAgencies.length > 0) {
          /** After filtering by LOB and REGION, if INTERNAL, prepend the super agency */
          this.availableAgencies = (() => {
            const isInternalRole = this.user.basicUserInfo.userType.toLowerCase() === USER_ROLE.INTERNAL.toLowerCase();
            const regionAndLOBFilteredAgencies = lobFilteredAgencies.filter(a => (a.region === formValue.region));
            if (isInternalRole) {
              const superAgency = lobFilteredAgencies[0];
              return [superAgency].concat(regionAndLOBFilteredAgencies);
            } else {
              return regionAndLOBFilteredAgencies;
            }
          })();

          this.availableAgencies = this.availableAgencies.sort((a,b)=> a.agencyName < b.agencyName  ? -1 : 1)

          if (this.availableRegions.length > 0 && this.availableAgencies.length > 0) {
            this.formControls.region.enable({emitEvent: false});
            this.formControls.agency.enable({emitEvent: false});
          }
        }
      }
    );
  }

  get formValuesAsJson(): EntitlementsInfoFormValuesAsJsonInterface {
    return (this.entitlementsInfoForm
      .value as unknown) as EntitlementsInfoFormValuesAsJsonInterface;
  }

  get formControls(): EntitlementsInfoFormControls {
    return (this.entitlementsInfoForm
      .controls as unknown) as EntitlementsInfoFormControls;
  }

  getAvailableLinesOfBusiness() {
    const linesOfBusiness = [];
    if (this.user.userInfo.agencies && this.user.userInfo.agencies.length > 0)  {
      // All non-HM ASPs don't have Broker360 Individual and Senior
      if (environment.initData.brand.ASPID === "HM") {
        linesOfBusiness.push(LINE_OF_BUSINESS_ENUM.SMALL_GROUP);
      } else {
        linesOfBusiness.push(LINE_OF_BUSINESS_ENUM.INDIVIDUAL);
      }
    }
    if (this.user.userInfo.broker360ACAAgencies && this.user.userInfo.broker360ACAAgencies.length > 0) {
      linesOfBusiness.push(LINE_OF_BUSINESS_ENUM.INDIVIDUAL);
    }
    if (this.user.userInfo.broker360Agencies && this.user.userInfo.broker360Agencies.length > 0) {
      linesOfBusiness.push(LINE_OF_BUSINESS_ENUM.SENIOR);
    }

    const uniqArray = ((array: any[]) => array.filter((item, index, inputArray) => inputArray.indexOf(item) === index));

    return uniqArray(linesOfBusiness);
  }

  getValidCertifications(): CertificationInterface[] {
    const userData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(
      LOGIN_INFO_REDUCER_NAME
    );

    const validCertifications = [];

    const tmpDate = new Date();
    const currentYear = tmpDate.getFullYear();
    const certsDate = new Date('01/01/' + currentYear);

    const certsOnRecord = userData.userInfo.certifications;
    for (const key in certsOnRecord) {
      if (certsOnRecord[key]) {
        const endDate = new Date(certsOnRecord[key].endDate);
        // const endDate = new Date('2022-12-01');

        if (certsDate <= endDate) {
          validCertifications.push(certsOnRecord[key]);
        }
      }
    }
    return validCertifications;
  }

  setupUserTypeForDisplay() {
    const activeAgency = this.getSelectedAgencyWithEntitlements()

    if (!activeAgency) {
      return;
    }

    const hasActiveEntitlement = (
      agency: BaseAgency,
      entitlementName: ENTITLEMENTS
    ) => {
      const matchedEntitlement = agency.entitlements.find(
        e => e.descriptionKey.toUpperCase() === entitlementName.toUpperCase()
      );
      return matchedEntitlement && matchedEntitlement.hasEntitlement;
    };

    let userType = '';

    //checking if certified
    if (this.validCertifications.length > 0 && this.hasFFM() && this.hasNPN()) {
      //checking if telesales or retail
      if (
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.TELESALES_AGENT_DP) ||
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.RETAIL_STORE)
      ) {
        userType = 'Certified Telesales/Retail Store Agent';
        //checking if certified gen admin
      } else if (
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.GENERAL_AGENCY_ADMIN)
      ) {
        userType = 'Certified General Agency Admin';
        //you're a certified agent
      } else {
        userType = 'Certified Agent';
      }
    } else {
      if (
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.TELESALES_AGENT_DP) ||
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.RETAIL_STORE)
      ) {
        userType = 'Non-Certified Telesales/Retail Store Agent';
      } else if (
        hasActiveEntitlement(activeAgency, ENTITLEMENTS.GENERAL_AGENCY_ADMIN)
      ) {
        userType = 'General Agency Admin';
      } else {
        if (this.hasFFM() && this.hasNPN()) {
          userType = 'Non-Certified Agent';
        } else {
          userType = 'N/A';
        }
      }
    }

    this.userType = userType;
  }

  /** Internal users won't have entitlements on their selected agency, so they need to reference the super agency */
  getSelectedAgencyWithEntitlements(): BaseAgency {
    const isInternalRole = this.user.basicUserInfo.userType.toLowerCase() === USER_ROLE.INTERNAL;
    return isInternalRole ? this.availableAgencies[0] : this.formValuesAsJson.agency;
  }

  checkCerts() {
    // return !!(this.validCertifications.length > 0);
    return true;
  }
  hasFFM() {
    // return !!(this.user.userInfo && this.user.userInfo.ffmId);
    return true;
  }
  hasNPN() {
    return !!this.user.userInfo.npnNo;
  }

  getActiveEntitlements(filteredAgency: BaseAgency): Entitlement[] {
    return filteredAgency.entitlements.filter(e => e.hasEntitlement);
  }

  trackAgencyComparison(option: BaseAgency, value: BaseAgency) {
    return option.agencyId === value.agencyId && option.region === value.region;
  }
}
